import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, User, Calendar, TrendingUp, Activity, Trophy, AlertTriangle } from 'lucide-react'
import { useStatsData } from '../hooks/useStatsData'
import WinRateChart from '../components/WinRateChart'
import LpzChart from '../components/LpzChart'
import SetResultsChart from '../components/SetResultsChart'
import MatchList from '../components/MatchList'
import LpzBracketChart from '../components/LpzBracketChart'

const PlayerDetail = () => {
  const { playerId } = useParams()
  const { data: player, loading, error } = useStatsData(playerId)

  if (loading) return (
    <div className="text-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading player data...</p>
    </div>
  )
  if (error) return <div className="text-red-600 text-center py-8">Error: {error}</div>
  if (!player) return <div className="text-center py-8">Player not found</div>

  const { player_name, match_context, win_rate, match_list, set_results, comeback } = player

  // Calculate days since last match
  const getDaysSinceLastMatch = (lastMatchDate) => {
    if (!lastMatchDate) return 'Unknown'
    const today = new Date()
    const lastMatch = new Date(lastMatchDate)
    const diffTime = today - lastMatch
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const daysSinceLastMatch = getDaysSinceLastMatch(match_context.last_match)

  return (
    <div>
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Player Selection
        </Link>
      </div>

      {/* Player Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl shadow-lg p-8 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2">{player_name}</h1>
          </div>
          <div className="text-right">
            <div className="text-xl text-blue-100">Player ID: {playerId}</div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {match_context.number_of_matches} matches
              </div>
              <div className="text-sm text-gray-600">
                over the last {match_context.date_range_days} days
              </div>
            </div>
            <TrendingUp className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {match_context.matches_per_month} matches per month
              </div>
              <div className="text-sm text-gray-600">
                last match {daysSinceLastMatch} days ago
              </div>
            </div>
            <Calendar className="h-10 w-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-gray-900">
                {(win_rate.overall * 100).toFixed(1)}%
              </div>
              <div className="text-gray-600">Overall Win Rate</div>
            </div>
            <Activity className="h-10 w-10 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="space-y-8">
        <WinRateChart
          data={win_rate.trend}
          matchList={match_list?.matches || []}
          title="Win Rate Trend (10-Match Rolling Window)"
        />

        <LpzChart
          data={match_list?.matches || []}
          title="LPZ Rating Progression"
        />

        <LpzBracketChart
          data={player.lpz_bracket}
          title="Win Rate by Opponent LPZ"
        />

        {/* Streaks Tile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Streaks Tile */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Streaks</h4>
                <p className="text-sm text-gray-600 mb-3">Longest consecutive results</p>
                {player.streaks ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-green-700 font-bold">Win Streak:</span>
                      <span className="text-2xl font-bold text-green-700">{player.streaks.longest_win_streak}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-700 font-bold">Loss Streak:</span>
                      <span className="text-2xl font-bold text-red-700">{player.streaks.longest_loss_streak}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 italic">No streak data</div>
                )}
              </div>
              <TrendingUp className="h-10 w-10 text-indigo-500" />
            </div>
          </div>
          {/* Favorite Opponents Tile */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-400">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Favorite Opponents</h4>
                <p className="text-sm text-gray-600 mb-3">Best win/loss record</p>
                {player.opponents?.favorite && player.opponents.favorite.length > 0 ? (
                  <ul className="text-sm text-gray-800">
                    {player.opponents.favorite.map((op, idx) => (
                      <li key={idx} className="mb-1">
                        <span className="font-bold">{op.opponent}</span> {op.wins}:{op.losses} Matches {op.sets_won}:{op.sets_lost} Sets
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500 italic">No data</div>
                )}
              </div>
              <User className="h-10 w-10 text-green-400" />
            </div>
          </div>
          {/* Least Favorite Opponents Tile */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-400">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Nemesis Opponents</h4>
                <p className="text-sm text-gray-600 mb-3">Worst win/loss record</p>
                {player.opponents?.nemesis && player.opponents.nemesis.length > 0 ? (
                  <ul className="text-sm text-gray-800">
                    {player.opponents.nemesis.map((op, idx) => (
                      <li key={idx} className="mb-1">
                        <span className="font-bold">{op.opponent}</span> {op.wins}:{op.losses} Matches {op.sets_won}:{op.sets_lost} Sets
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500 italic">No data</div>
                )}
              </div>
              <User className="h-10 w-10 text-red-400" />
            </div>
          </div>
        </div>

        <SetResultsChart 
          data={set_results?.results || []}
          title="Set Results Distribution"
        />
        {/* Comeback Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Comebacks Tile */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Comebacks</h4>
                <p className="text-sm text-gray-600 mb-3">Matches won after 0:2</p>
                {comeback?.comeback_wins?.total_0_2_starts > 0 ? (
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {comeback.comeback_wins.count} of {comeback.comeback_wins.total_0_2_starts}
                    </div>
                    <div className="text-sm text-green-600 font-medium">
                      {(comeback.comeback_wins.percentage * 100).toFixed(1)}% success rate
                    </div>
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{width: `${comeback.comeback_wins.percentage * 100}%`}}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 italic">Never happened</div>
                )}
              </div>
              <Trophy className="h-10 w-10 text-green-500" />
            </div>
          </div>

          {/* Blown Leads Tile */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Blown Leads</h4>
                <p className="text-sm text-gray-600 mb-3">Matches lost after 2:0</p>
                {comeback?.blown_leads?.total_2_0_starts > 0 ? (
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {comeback.blown_leads.count} of {comeback.blown_leads.total_2_0_starts}
                    </div>
                    <div className="text-sm text-red-600 font-medium">
                      {(comeback.blown_leads.percentage * 100).toFixed(1)}% failure rate
                    </div>
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{width: `${comeback.blown_leads.percentage * 100}%`}}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 italic">Never happened</div>
                )}
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
          </div>

          {/* Deuce Sets Tile */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Deuce Sets</h4>
                <p className="text-sm text-gray-600 mb-3">Sets played at deuce (10:10+)</p>
                {player.deuce?.total_deuce_sets > 0 ? (
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {player.deuce.deuce_sets_won} of {player.deuce.total_deuce_sets}
                    </div>
                    <div className="text-sm text-blue-600 font-medium">
                      {(player.deuce.deuce_win_percentage * 100).toFixed(2)}% win rate
                    </div>
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{width: `${(player.deuce.deuce_win_percentage * 100).toFixed(2)}%`}}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 italic">No deuce sets</div>
                )}
              </div>
              <User className="h-10 w-10 text-blue-500" />
            </div>
          </div>
        </div>
        
        {/* Match List Section */}
        <MatchList matchData={match_list} />
      </div>
    </div>
  )
}

export default PlayerDetail